import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

const overlayVariants = {
  closed: { opacity: 0, backdropFilter: 'blur(0px)', transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] } },
  open: { opacity: 1, backdropFilter: 'blur(14px)', transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
};

const panelVariants = {
  closed: {
    opacity: 0,
    scale: 0.78,
    y: 56,
    filter: 'blur(10px)',
    transition: { duration: 0.42, ease: [0.4, 0, 0.2, 1] },
  },
  open: {
    opacity: 1,
    scale: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { type: 'spring', damping: 24, stiffness: 260, mass: 0.9 },
  },
};

const GiftReserveModal = ({
  gift,
  isOpen,
  onClose,
  onExited,
  reservationMessage,
  reservationData,
  setReservationData,
  onSubmit,
}) => {
  const closeBtnRef = useRef(null);
  const [mounted, setMounted] = useState(isOpen);
  const openRef = useRef(isOpen);

  useEffect(() => {
    openRef.current = isOpen;
    if (isOpen) setMounted(true);
  }, [isOpen]);

  useEffect(() => {
    if (!mounted || !isOpen) return undefined;
    document.body.style.overflow = 'hidden';
    const t = requestAnimationFrame(() => closeBtnRef.current?.focus());
    return () => {
      cancelAnimationFrame(t);
      if (!openRef.current) document.body.style.overflow = '';
    };
  }, [mounted, isOpen]);

  const handlePanelComplete = () => {
    if (!openRef.current) {
      document.body.style.overflow = '';
      setMounted(false);
      onExited?.();
    }
  };

  if (!mounted) return null;

  return createPortal(
    <motion.div
      className="modal-overlay modal-overlay--animated gift-reserve-overlay"
      variants={overlayVariants}
      initial="closed"
      animate={isOpen ? 'open' : 'closed'}
      onClick={onClose}
    >
      <motion.div
        className="modal-content small"
        variants={panelVariants}
        initial="closed"
        animate={isOpen ? 'open' : 'closed'}
        onAnimationComplete={handlePanelComplete}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Reserve {gift.name}</h3>
          <motion.button
            ref={closeBtnRef}
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close"
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
          >
            <X size={22} />
          </motion.button>
        </div>
        <div className="modal-body">
          {reservationMessage && (
            <div className={`reservation-message ${reservationMessage.includes('Thank') ? 'success' : 'error'}`}>
              {reservationMessage}
            </div>
          )}
          <form onSubmit={onSubmit}>
            <div className="form-group">
              <label>Your Name *</label>
              <input
                type="text"
                required
                value={reservationData.guest_name}
                onChange={(e) => setReservationData({ ...reservationData, guest_name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                required
                value={reservationData.guest_email}
                onChange={(e) => setReservationData({ ...reservationData, guest_email: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Phone *</label>
              <input
                type="tel"
                required
                value={reservationData.guest_phone}
                onChange={(e) => setReservationData({ ...reservationData, guest_phone: e.target.value })}
              />
            </div>
            <button type="submit" className="btn-primary">Confirm Reservation</button>
          </form>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default GiftReserveModal;
